/**
 * ClubRoom - "Club" (DJ) immersive player: nightclub stage with sweeping laser
 * beams, spotlights, a spinning DJ turntable (cover = vinyl), reactive EQ bars
 * and a crowd silhouette. Reuses ActionBar for Vote/Share/etc.
 */
import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, Disc3 } from 'lucide-react';
import ActionBar from './ActionBar';
import { cn } from '../utils/helpers';

const LASERS = ['#FF007F', '#00F0FF', '#39FF14', '#B14BFF', '#FFD700'];

const ClubRoom = ({
  room,
  isPlaying,
  onPlay,
  onPause,
  energyLevel = 60,
  currentTrack,
  navHeight = 0,
}) => {
  const navigate = useNavigate();
  const thisRoomPlaying = currentTrack && (
    currentTrack.artist_id === room?.id || currentTrack.uploader_id === room?.id
  );
  const artwork = (thisRoomPlaying && currentTrack?.artwork) || room?.artistImage;
  const haptic = () => { try { navigator.vibrate && navigator.vibrate(12); } catch (e) {} };

  const openArtist = () => {
    const uid = room?.artistUserId;
    if (!uid || uid.startsWith('admin_uploaded')) return;
    navigate(`/profile/${uid}`);
  };

  // Beat engine — drives a shared --beat CSS var so lasers/EQ/glow/dancers pulse
  // together in time. Synced to playback (safe on iOS; no audio graph rerouting).
  const rootRef = useRef(null);
  useEffect(() => {
    let raf; const start = performance.now(); const beatMs = 60000 / 124;
    const loop = (t) => {
      const el = rootRef.current;
      if (el) {
        let beat = 0;
        if (isPlaying) {
          const n = Math.floor((t - start) / beatMs);
          const phase = ((t - start) % beatMs) / beatMs;
          const env = Math.pow(1 - phase, 2.3);
          const accent = n % 4 === 0 ? 1 : (n % 2 === 0 ? 0.82 : 0.66);
          beat = env * accent;
        }
        el.style.setProperty('--beat', beat.toFixed(3));
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);

  return (
    <div ref={rootRef} className="club-scene absolute inset-0 overflow-hidden bg-[#040207]" data-testid={`club-room-${room?.id}`}>
      <style>{`
        .club-scene { --beat: 0; }
        .club-glow-beat { opacity: calc(0.22 + var(--beat) * 0.78); transform: scale(calc(1 + var(--beat) * 0.05)); transition: opacity 80ms linear, transform 80ms linear; }
        .club-laser-beat { opacity: calc(0.24 + var(--beat) * 0.62); transition: opacity 80ms linear; }
        .club-eq-bar { transform-origin: bottom; transform: scaleY(calc(0.45 + var(--beat) * 1.0)); transition: transform 90ms linear; }
        .club-dancer { transform: translateY(calc(var(--beat) * -14px)) scaleY(calc(1 + var(--beat) * 0.08)); transition: transform 90ms linear; }
      `}</style>
      {/* Deep stage gradient */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 80% at 50% 0%, #1a0630 0%, #060010 55%, #040207 100%)' }} />

      {/* Sweeping laser beams from the ceiling */}
      <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{ top: -40 }}>
        {LASERS.map((c, i) => (
          <motion.div
            key={i}
            className="absolute origin-top"
            style={{ left: 0, top: 0 }}
            animate={{ rotate: i % 2 === 0 ? [ -28 + i * 6, 28 - i * 4, -28 + i * 6 ] : [ 26 - i * 5, -26 + i * 5, 26 - i * 5 ] }}
            transition={{ duration: 6 + i * 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div
              className="club-laser-beat w-[3px] rounded-full"
              style={{
                height: '78vh',
                background: `linear-gradient(to bottom, ${c} 0%, ${c}00 85%)`,
                filter: 'blur(1px)',
                boxShadow: `0 0 24px ${c}`,
              }}
            />
          </motion.div>
        ))}
      </div>

      {/* Roaming spotlights */}
      <motion.div
        className="absolute pointer-events-none rounded-full"
        style={{ width: 380, height: 380, background: 'radial-gradient(circle, rgba(177,75,255,0.22) 0%, transparent 70%)', top: '18%' }}
        animate={{ x: ['-30%', '60%', '-30%'] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute pointer-events-none rounded-full"
        style={{ width: 320, height: 320, background: 'radial-gradient(circle, rgba(0,240,255,0.18) 0%, transparent 70%)', top: '30%', right: 0 }}
        animate={{ x: ['20%', '-60%', '20%'] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Center: DJ turntable */}
      <div
        className="absolute inset-x-0 flex flex-col items-center justify-start overflow-y-auto scrollbar-hide px-6"
        style={{ top: navHeight ? navHeight + 56 : 176, bottom: 210 }}
      >
        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-4 mt-2 flex items-center gap-1.5">
          <Disc3 className="w-3.5 h-3.5 text-[#FF007F]" /> Now Spinning
        </p>

        {/* Turntable platter */}
        <div className="relative" style={{ width: 200, height: 200 }}>
          <div className="absolute inset-0 rounded-full bg-[#0a0a0f] shadow-[0_24px_60px_rgba(0,0,0,0.7)] border border-white/10" />
          <div
            className="club-glow-beat absolute -inset-2.5 rounded-full pointer-events-none"
            style={{ boxShadow: '0 0 50px rgba(255,0,127,0.45)' }}
          />
          <button
            onClick={openArtist}
            data-testid="club-turntable-cover"
            className={cn(
              'absolute inset-3 rounded-full overflow-hidden ring-2 ring-white/15',
              'animate-[spin_5s_linear_infinite]',
              !isPlaying && '[animation-play-state:paused]'
            )}
          >
            {artwork ? (
              <img src={artwork} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#B14BFF] to-[#FF007F] flex items-center justify-center text-4xl font-black text-white">
                {room?.artistName?.charAt(0) || '?'}
              </div>
            )}
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-[#040207] ring-2 ring-white/30" />
          </button>
          {/* tonearm */}
          <div className="absolute -right-1 top-1 w-20 h-20 pointer-events-none" style={{ transformOrigin: 'top right', transform: isPlaying ? 'rotate(-18deg)' : 'rotate(-4deg)', transition: 'transform 0.6s' }}>
            <div className="absolute right-2 top-1 w-2.5 h-2.5 rounded-full bg-white/40" />
            <div className="absolute right-3 top-1.5 w-[3px] h-14 bg-white/30 rounded-full rotate-[35deg] origin-top" />
          </div>
          {/* Play / pause — offset to the side so it never covers the cover art */}
          <motion.button
            whileTap={{ scale: 0.85 }}
            onClick={() => { haptic(); isPlaying ? onPause() : onPlay(); }}
            data-testid="club-play-btn"
            className="absolute -bottom-2 -right-2 z-20 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-[0_8px_30px_rgba(0,0,0,0.6)] border-2 border-[#040207]"
            style={{ background: 'linear-gradient(135deg,#FF007F,#B14BFF)' }}
          >
            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
          </motion.button>
        </div>

        {/* Reactive EQ bars */}
        <div className="mt-7 flex items-end gap-1 h-8" aria-hidden>
          {[0.5, 0.85, 0.4, 1, 0.6, 0.9, 0.5, 0.75, 0.45, 0.95, 0.55, 0.8].map((h, i) => (
            <span
              key={i}
              className="club-eq-bar w-1.5 rounded-full"
              style={{ background: LASERS[i % LASERS.length], height: `${28 + h * 60}%`, transitionDelay: `${(i % 6) * 10}ms` }}
            />
          ))}
        </div>

        {/* Artist / track — single clean block */}
        <h2 className="mt-5 text-2xl font-black text-white text-center truncate max-w-[82vw]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          {room?.artistName || 'NLT Radio'}
        </h2>
        {thisRoomPlaying && (
          <p className="mt-1 text-sm text-white/55 text-center truncate max-w-[82vw]">
            Playing <span className="text-[#FF007F] font-semibold">{currentTrack.track_title}</span>
          </p>
        )}
      </div>

      {/* Dancing crowd silhouettes */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ height: 200, zIndex: 5 }}>
        <div className="absolute bottom-0 left-0 right-0 h-40" style={{ background: 'linear-gradient(to top, #040207 42%, transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 flex items-end justify-around px-2">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => {
            const scale = 0.8 + (i % 3) * 0.18;
            const delay = (i % 4) * 0.25;
            const dur = 0.9 + (i % 3) * 0.35;
            return (
              <motion.div
                key={i}
                style={{ transformOrigin: 'bottom center', height: 130 * scale, opacity: 0.9 }}
                animate={isPlaying ? { rotate: i % 2 === 0 ? [-4, 4, -4] : [4, -4, 4] } : { rotate: 0 }}
                transition={{ duration: dur, repeat: Infinity, ease: 'easeInOut', delay }}
              >
                <div className="club-dancer" style={{ transitionDelay: `${(i % 4) * 15}ms` }}>
                <svg viewBox="0 0 60 150" width={60 * scale} height={130 * scale}>
                  <g fill="#000">
                    {/* raised arms for some dancers */}
                    {i % 2 === 0 ? (
                      <>
                        <rect x="8" y="30" width="7" height="45" rx="3.5" transform="rotate(20 11 40)" />
                        <rect x="45" y="30" width="7" height="45" rx="3.5" transform="rotate(-20 49 40)" />
                      </>
                    ) : (
                      <>
                        <rect x="10" y="55" width="7" height="42" rx="3.5" transform="rotate(-12 13 60)" />
                        <rect x="43" y="55" width="7" height="42" rx="3.5" transform="rotate(12 47 60)" />
                      </>
                    )}
                    <circle cx="30" cy="22" r="13" />
                    <rect x="19" y="40" width="22" height="70" rx="11" />
                    <rect x="21" y="95" width="8" height="55" rx="4" />
                    <rect x="31" y="95" width="8" height="55" rx="4" />
                  </g>
                </svg>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Action bar (Vote / Share / etc.) */}
      <ActionBar room={room} currentTrack={currentTrack} />
    </div>
  );
};

export default ClubRoom;
